import { NotificationContainer } from 'react-notifications';

import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
  useNavigate,
} from 'react-router-dom';

// custom components
import { NotFound, PermissionDenied } from './components/errorStatus';
import { SignInContent } from './components/rightContent';
import { ROUTES } from './utils/constant';
// account components
import Account from './containers/account';
// activities components
import Activities from './containers/activities';
import ActivityParticipants from './containers/activities/participants';
import ActivityPreview from './containers/activities/library/preview';
import ActivitySteps from './containers/activities/steps';
//admin components
import ActivitySettingAdmin from './containers/admin/activitySettings/index';
import SystemFeatures from './containers/admin/systemFeatures/index';
import Logs from './containers/admin/logs/index';
import SystemReports from './containers/admin/systemReports/index';
import SystemSetting from './containers/admin/systemSettings/index';
import UserOrganization from './containers/admin/userOrganization/index';
import AdminActivities from './containers/admin/activities';
import AuditPage from './containers/admin/audit/index';
import AdminReportBuilder from './containers/reportBuilder/index';
import OutcomeRelationship from './containers/reportBuilder/OutcomeRelationship';
import AccountDisable from './containers/auth/accountDisable';
import ForgotPassword from './containers/auth/forgotPass';
import UserRegistration from './containers/auth/userRegistration';
import Help from './containers/auth/help';
import InviteLinkExpired from './containers/auth/inviteLinkExpired';
import PasswordLinkExpired from './containers/auth/passwordLinkExpired';
import AuthLayout from './containers/auth/layout';
import MailVerification from './containers/auth/mailVerification';
import OrganizationWelcome from './containers/auth/organizationWelcome';
import PasswordChangeSuccess from './containers/auth/passwordChangedSuccess';
import Profile from './containers/auth/profile';
import ResetPassword from './containers/auth/resetPass';
// user components
import SignIn from './containers/auth/signIn';
import SignUp from './containers/auth/signUp';
import WelcomeToWhitebox from './containers/auth/welcomeToWhitebox';
// browse components
import BrowseActivities from './containers/browser/browseActivities';
// root components
import Dashboard from './containers/dashboard';
import Layout from './containers/layout';
// organization components
import Organization from './containers/organization';
// Question Generator components
import questionGenerator from './containers/questionGenerator';
// report components
import Reports from './containers/reports';
// survey components
import Surveys from './containers/survey';
import { persistor } from './store';
import { userAuthMgr } from './utils/auth';
import { get } from './utils/lodash';
import { useSelector } from 'react-redux';
import withTracker from './lib/withPageTracker';
import verification from './containers/auth/verification';
import NavigationWrapper from './components/navigationWrapper';
import { useTranslation } from 'react-i18next';

if (!userAuthMgr.isAuthenticated) {
  persistor.purge();
}

const NoMatch = (props) => {
  const navigate = useNavigate();
  return <NotFound navigate={navigate} {...props} />;
};

const PrivateRoute = ({ component: Component, permission, ...rest }) => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.authentication.user);
  const permissions = get(user, 'features', []);
  const featureAllowed =
    rest.features && rest.features.some((i) => permissions.includes(i));
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  return userAuthMgr.isAuthenticated ? (
    <div id="pg-route-private">
      <Layout
        {...rest}
        routes={ROUTES(t)}
        title={rest.title}
        sub={rest.sub}
        match={{ pathname: location.pathname, path: rest.path, params: params }}
        route={{ permission: permissions }}
        navigate={navigate}
      >
        {rest.features ? (
          featureAllowed ? (
            <Component
              key={params.id}
              route={{ permission: permissions }}
              match={{
                pathname: location.pathname,
                path: rest.path,
                params: params,
                search: location.search,
              }}
              navigate={navigate}
            />
          ) : (
            <PermissionDenied />
          )
        ) : (
          <Component
            key={params.id}
            route={{ permission: permissions }}
            match={{
              pathname: location.pathname,
              path: rest.path,
              params: params,
              search: location.search,
            }}
            navigate={navigate}
          />
        )}
      </Layout>
      <NotificationContainer />
    </div>
  ) : (
    <Navigate
      to={{
        pathname: '/',
        state: {
          from: location,
          redirectRoute: location,
          logoutReason: '',
        },
      }}
      navigate={navigate}
    />
  );
};

const SuperAdminRoute = ({ component: Component, permission, ...rest }) => {
  const { t } = useTranslation();
  let location = useLocation();
  const params = useParams();
  const user = useSelector((state) => state.authentication.user);
  const permissions = get(user, 'features', []);
  const navigate = useNavigate();

  return userAuthMgr.isAuthenticated ? (
    <div id="pg-route-super-admin">
      <Layout
        routes={ROUTES(t)}
        title={rest.title}
        sub={rest.sub}
        match={{ pathname: location.pathname, path: rest.path, params: params }}
        route={{ permission: permissions }}
        navigate={navigate}
      >
        <Component
          match={{
            pathname: location.pathname,
            path: rest.path,
            params: params,
          }}
        />
      </Layout>
      <NotificationContainer />
    </div>
  ) : (
    <Navigate
      to={{
        pathname: '/',
        state: { from: location },
      }}
    />
  );
};

const AuthRoute = ({ component: Component, ...rest }) => {
  let location = useLocation();
  const params = useParams();
  const user = useSelector((state) => state.authentication.user);
  const permissions = get(user, 'features', []);
  const navigate = useNavigate();

  const WrappedComponent = userAuthMgr.isAuthenticated ? (
    <Navigate
      to={{
        pathname: '/dashboard',
        state: { from: location },
      }}
    />
  ) : (
    <div id="pg-route-auth-layout">
      <AuthLayout
        match={{
          pathname: location.pathname,
          path: rest.path,
          params: params,
        }}
        location={location}
        route={{ permission: permissions }}
        navigate={navigate}
        {...rest}
      >
        <Component
          match={{
            pathname: location.pathname,
            path: rest.path,
            params: params,
          }}
          location={location}
          route={{ permission: permissions }}
        />
      </AuthLayout>
      <NotificationContainer />
    </div>
  );

  return WrappedComponent;
};

const PublicRoute = ({ component: Component, ...rest }) => {
  let location = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  return (
    <div id="pg-route-public">
      <AuthLayout
        location={location}
        match={{
          pathname: location.pathname,
          path: rest.path,
          params: params,
        }}
        navigate={navigate}
        {...rest}
      >
        <Component
          match={{
            pathname: location.pathname,
            path: rest.path,
            params: params,
          }}
          location={location}
          navigate={navigate}
        />
      </AuthLayout>
      <NotificationContainer />
    </div>
  );
};

const CustomRoute = ({ component: Component, ...rest }) => {
  let location = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  return (
    <div id="pg-route-custom">
      <Component
        match={{ pathname: location.pathname, path: rest.path, params: params }}
        location={location}
        navigate={navigate}
        {...rest}
      />
      <NotificationContainer />
    </div>
  );
};

const AppRouting = () => {
  const { t } = useTranslation();
  return (
    <Routes>
      <Route
        exact
        path="/"
        element={
          <AuthRoute
            exact
            path="/"
            component={withTracker(SignIn)}
            rightComponent={<SignInContent />}
          />
        }
      />
      <Route
        path="/signin"
        element={
          <AuthRoute
            exact
            path="/signin"
            component={withTracker(SignIn)}
            rightComponent={<SignInContent />}
          />
        }
      />

      <Route
        path="/dashboard"
        element={
          <PrivateRoute
            exact
            path="/dashboard"
            component={withTracker(Dashboard)}
            title={t('common.dashboard')}
            features={['dashboard']}
          />
        }
      />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute
            exact
            path="/dashboard"
            component={withTracker(Dashboard)}
            title={t('routing.dashboard')}
            features={['dashboard']}
          />
        }
      />

      <Route
        path="/forgot-password"
        element={
          <PublicRoute
            exact
            path="/forgot-password"
            component={withTracker(ForgotPassword)}
            rightComponent={<SignInContent />}
          />
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute
            exact
            path="/signup"
            component={withTracker(SignUp)}
            rightComponent={<SignInContent />}
          />
        }
      />
      <Route
        path="/verification"
        element={
          <PublicRoute
            exact
            path="/verification"
            component={withTracker(verification)}
            fullPage
          />
        }
      />
      <Route
        path="/organization-welcome"
        element={
          <PublicRoute
            exact
            path="/organization-welcome"
            component={withTracker(OrganizationWelcome)}
            fullPage
          />
        }
      />
      <Route
        path="/welcome-takso"
        element={
          <PublicRoute
            exact
            path="/welcome-takso"
            component={withTracker(WelcomeToWhitebox)}
            fullPage
          />
        }
      />
      <Route
        path="/password-changed"
        element={
          <PublicRoute
            exact
            path="/password-changed"
            component={withTracker(PasswordChangeSuccess)}
            fullPage
          />
        }
      />
      <Route
        path="/link-expired"
        element={
          <PublicRoute
            exact
            path="/link-expired"
            component={withTracker(InviteLinkExpired)}
            fullPage
          />
        }
      />
      <Route
        path="/password-link-expired"
        element={
          <PublicRoute
            exact
            path="/password-link-expired"
            component={withTracker(PasswordLinkExpired)}
            fullPage
          />
        }
      />
      <Route
        path="/reset-password/:id"
        element={
          <PublicRoute
            exact
            path="/reset-password/:id"
            component={withTracker(ResetPassword)}
            rightComponent={<SignInContent />}
          />
        }
      />
      <Route
        path="/invite/*"
        element={
          <PublicRoute
            exact
            path="/invite/*"
            component={withTracker(UserRegistration)}
            rightComponent={<SignInContent />}
          />
        }
      />
      <Route
        path="/account-disabled"
        element={
          <PublicRoute
            exact
            path="/account-disabled"
            component={withTracker(AccountDisable)}
            fullPage
          />
        }
      />
      <Route
        path="/account/verification/:token"
        element={
          <PublicRoute
            exact
            path="/account/verification/:token"
            component={withTracker(MailVerification)}
            fullPage
          />
        }
      />
      <Route
        path="/survey/:surveyId"
        element={
          <CustomRoute
            exact
            path="/survey/:surveyId"
            component={withTracker(Surveys)}
          />
        }
      />
      <Route
        path="/generate-question"
        element={
          <CustomRoute
            exact
            path="/generate-question"
            component={withTracker(questionGenerator)}
          />
        }
      />
      <Route
        path="/preview-survey/:surveyId"
        element={
          <PrivateRoute
            exact
            path="/preview-survey/:surveyId"
            component={withTracker(Surveys)}
            title="Preview Survey"
            features={['activities']}
          />
        }
      />

      <Route
        path="/activities"
        element={
          <PrivateRoute
            exact
            path="/activities"
            component={withTracker(Activities)}
            title={t('routing.activities')}
            features={['activities']}
          />
        }
      />
      <Route
        path="/activity/:id/preview"
        element={
          <PrivateRoute
            exact
            path="/activity/:id/preview"
            component={withTracker(ActivityPreview)}
            title={t('routing.activity_preview')}
            sub
            features={['activities']}
          />
        }
      />
      <Route
        path="/activity/:id/participants"
        element={
          <PrivateRoute
            exact
            path="/activity/:id/participants"
            component={withTracker(ActivityParticipants)}
            title={t('routing.activity_participants')}
            sub
            features={['activity_participants']}
          />
        }
      />

      <Route
        path="/activity/:id/participants"
        element={
          <PrivateRoute
            exact
            path="/activity/:id/participants"
            component={withTracker(ActivitySteps)}
            title={t('routing.activity')}
            sub
            features={['activities']}
          />
        }
      />

      <Route
        path="/activities/:id"
        element={
          <PrivateRoute
            exact
            path="/activities/:id"
            component={withTracker(ActivitySteps)}
            title={t('routing.activity')}
            sub
            features={['activities']}
          />
        }
      />
      <Route
        path="/activities/:id/:type"
        element={
          <PrivateRoute
            exact
            path="/activities/:id/:type"
            component={withTracker(ActivitySteps)}
            title={t('routing.activity')}
            sub
            features={['activities']}
          />
        }
      />
      <Route
        path="/organisation"
        element={
          <PrivateRoute
            exact
            path="/organisation"
            component={withTracker(Organization)}
            title={t('common.organisation')}
            features={['account']}
          />
        }
      />
      <Route
        path="/organisation/activity/search"
        element={
          <PrivateRoute
            exact
            path="/organisation/activity/search"
            component={withTracker(Organization)}
            title="Organisation - All Activities"
            features={['account']}
          />
        }
      />
      <Route
        path="/organisation/activity/by-user"
        element={
          <PrivateRoute
            exact
            path="/organisation/activity/by-user"
            component={withTracker(Organization)}
            title="Organisation - Users"
            features={['account']}
          />
        }
      />
      <Route
        path="/organisation/:type"
        element={
          <PrivateRoute
            exact
            path="/organisation/:type"
            component={withTracker(Organization)}
            title={t('routing.organisation')}
            features={['account']}
          />
        }
      />
      <Route
        path="/account"
        element={
          <PrivateRoute
            exact
            path="/account"
            component={withTracker(Account)}
            title={t('routing.account')}
            features={['account']}
          />
        }
      />
      <Route
        path="/account/:type"
        element={
          <PrivateRoute
            exact
            path="/account/:type"
            component={withTracker(Account)}
            title={t('routing.account')}
            features={['account']}
          />
        }
      />
      <Route
        path="/reports"
        element={
          <PrivateRoute
            exact
            path="/reports"
            component={withTracker(Reports)}
            title={t('routing.reports')}
            features={['report']}
          />
        }
      />
      <Route
        path="/reports/:type"
        element={
          <PrivateRoute
            exact
            path="/reports/:type"
            component={withTracker(Reports)}
            title={t('routing.reports')}
            features={['report']}
          />
        }
      />
      <Route
        path="/user-profile"
        element={
          <PrivateRoute
            exact
            path="/user-profile"
            component={withTracker(Profile)}
            title={t('routing.user_profile')}
          />
        }
      />
      <Route
        path="/admin/activities"
        element={
          <SuperAdminRoute
            exact
            path="/admin/activities"
            component={withTracker(AdminActivities)}
            title="Activities"
          />
        }
      />
      <Route
        path="/admin/activities/:id"
        element={
          <SuperAdminRoute
            exact
            path="/admin/activities/:id"
            component={withTracker(ActivitySteps)}
            title="Activity"
            sub
          />
        }
      />
      <Route
        path="/admin/activities/:id/:type"
        element={
          <SuperAdminRoute
            exact
            path="/admin/activities/:id/:type"
            component={withTracker(ActivitySteps)}
            title="Activity"
            sub
          />
        }
      />
      <Route
        path="/admin/activity/:id/preview"
        element={
          <SuperAdminRoute
            exact
            path="/admin/activity/:id/preview"
            component={withTracker(ActivityPreview)}
            title="Activity Preview"
            sub
          />
        }
      />
      <Route
        path="/admin/activity/:id/participants"
        element={
          <SuperAdminRoute
            exact
            path="/admin/activity/:id/participants"
            component={withTracker(ActivityParticipants)}
            title="Activity Participants"
            sub
          />
        }
      />
      <Route
        path="/admin/activity-settings"
        element={
          <SuperAdminRoute
            exact
            path="/admin/activity-settings"
            component={withTracker(ActivitySettingAdmin)}
            title={t('routing.activity_settings')}
          />
        }
      />
      <Route
        path="/admin/activity-settings/:type"
        element={
          <SuperAdminRoute
            exact
            path="/admin/activity-settings/:type"
            component={withTracker(ActivitySettingAdmin)}
            title={t('routing.activity_settings')}
          />
        }
      />
      <Route
        path="/admin/system-settings"
        element={
          <SuperAdminRoute
            exact
            path="/admin/system-settings"
            component={withTracker(SystemSetting)}
            title={t('routing.system_settings')}
          />
        }
      />
      <Route
        path="/admin/system-settings/:type"
        element={
          <SuperAdminRoute
            exact
            path="/admin/system-settings/:type"
            component={withTracker(SystemSetting)}
            title={t('routing.system_settings')}
          />
        }
      />
      <Route
        path="/admin/system-features"
        element={
          <SuperAdminRoute
            exact
            path="/admin/system-features"
            component={withTracker(SystemFeatures)}
            title={t('routing.system_features')}
          />
        }
      />
      <Route
        path="/admin/system-features/:type"
        element={
          <SuperAdminRoute
            exact
            path="/admin/system-features/:type"
            component={withTracker(SystemFeatures)}
            title={t('routing.system_features')}
          />
        }
      />
      <Route
        path="/admin/logs"
        element={
          <SuperAdminRoute
            exact
            path="/admin/logs"
            component={withTracker(Logs)}
            title={t('routing.logs')}
          />
        }
      />
      <Route
        path="/admin/logs/:type"
        element={
          <SuperAdminRoute
            exact
            path="/admin/logs/:type"
            component={withTracker(Logs)}
            title="Logs"
          />
        }
      />
      <Route
        path="/admin/system-logs"
        element={<Navigate to="/admin/logs/system" replace />}
      />
      <Route
        path="/admin/email-logs"
        element={<Navigate to="/admin/logs/email" replace />}
      />
      <Route
        path="/admin/accounts"
        element={
          <SuperAdminRoute
            exact
            path="/admin/accounts"
            component={withTracker(UserOrganization)}
            title={t('routing.accounts_users')}
          />
        }
      />
      <Route
        path="/admin/accounts/:type"
        element={
          <SuperAdminRoute
            exact
            path="/admin/accounts/:type"
            component={withTracker(UserOrganization)}
            title={t('routing.accounts_users')}
          />
        }
      />
      <Route
        path="/admin/system-reports"
        element={
          <SuperAdminRoute
            exact
            path="/admin/system-reports"
            component={withTracker(SystemReports)}
            title={t('routing.system_reports')}
          />
        }
      />
      <Route
        path="/admin/system-reports/:type"
        element={
          <SuperAdminRoute
            exact
            path="/admin/system-reports/:type"
            component={withTracker(SystemReports)}
            title={t('routing.system_reports')}
          />
        }
      />
      <Route
        path="/admin/audit"
        element={
          <SuperAdminRoute
            exact
            path="/admin/audit"
            component={withTracker(AuditPage)}
            title="Data Audit"
          />
        }
      />
      <Route
        path="/admin/report-builder"
        element={
          <SuperAdminRoute
            exact
            path="/admin/report-builder"
            component={withTracker(AdminReportBuilder)}
            title="Report Builder"
          />
        }
      />
      <Route
        path="/admin/report-builder/outcome-relationship"
        element={
          <SuperAdminRoute
            exact
            path="/admin/report-builder/outcome-relationship"
            component={withTracker(OutcomeRelationship)}
            title="Outcome Relationship"
          />
        }
      />
      <Route
        path="/help"
        element={
          <PrivateRoute
            exact
            path="/help"
            component={withTracker(Help)}
            title={t('routing.takso_outcomes_planning_platform_help')}
          />
        }
      />
      <Route
        path="/library"
        element={
          <PrivateRoute
            exact
            path="/library"
            component={withTracker(BrowseActivities)}
            title={t('routing.library')}
            features={['library']}
          />
        }
      />
      <Route
        path="/library/:id/preview"
        element={
          <PrivateRoute
            exact
            path="/library/:id/preview"
            component={withTracker(ActivityPreview)}
            title={t('routing.activity_preview')}
            sub
            features={['library']}
          />
        }
      />
      <Route path="*" element={<NoMatch />} />
    </Routes>
  );
};
export default AppRouting;
